import { useState, useEffect, useCallback } from 'react';

interface ColorSchemeOptions {
    storageKey?: string;
    transitionDuration?: number;
}

export function useColorScheme(options: ColorSchemeOptions = {}) {
    const {
        storageKey = 'colorScheme',
        transitionDuration = 150
    } = options;

    const [isDark, setIsDark] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            return JSON.parse(saved);
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    const [isTransitioning, setIsTransitioning] = useState(false);

    // Handle system color scheme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            if (!localStorage.getItem(storageKey)) {
                setIsDark(e.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [storageKey]);

    // Apply transitions when changing themes
    const toggleColorScheme = useCallback(() => {
        setIsTransitioning(true);
        
        // Add transition to relevant properties
        document.documentElement.style.transition = `
            background-color ${transitionDuration}ms ease-in-out,
            color ${transitionDuration}ms ease-in-out,
            border-color ${transitionDuration}ms ease-in-out
        `;

        setIsDark((prev: boolean) => {
            const newValue = !prev;
            localStorage.setItem(storageKey, JSON.stringify(newValue));
            return newValue;
        });

        // Remove transition after it's complete
        setTimeout(() => {
            document.documentElement.style.transition = '';
            setIsTransitioning(false);
        }, transitionDuration);
    }, [transitionDuration, storageKey]);

    // Apply dark mode class
    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDark);
    }, [isDark]);

    return {
        isDark,
        isTransitioning,
        toggleColorScheme
    };
}
